import jsPDF from 'jspdf';
import { GoodsReceipt } from '../types/goodsReceipt';

interface TemplateColorScheme {
  primary: string;
  secondary: string;
  text: string;
  background: string;
}

interface Template {
  templateCompanyName?: string;
  templateLogo?: string | null;
  logoBase64?: string | null;
  colorScheme?: TemplateColorScheme;
  fontFamily?: string;
}

interface GoodsReceiptPdfData {
  goodsReceipt: GoodsReceipt;
  companyName?: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  template?: Template;
}

const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
};

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

// Classic navy layout — mirrors the Invoice "classic" PDF design: left
// logo/company block, right title + meta table, boxed info panels, full-width
// dark table header, filled summary bar, bordered notes box.
export const generateGoodsReceiptPDF = (data: GoodsReceiptPdfData) => {
  const {
    goodsReceipt: grn,
    companyName: rawCompanyName = '',
    companyAddress = '',
    companyPhone = '',
    companyEmail = '',
    template
  } = data;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;

  const colorScheme = template?.colorScheme;
  const navy:  [number, number, number] = colorScheme?.primary    ? hexToRgb(colorScheme.primary)    : [30, 58, 95];
  const text:  [number, number, number] = colorScheme?.text       ? hexToRgb(colorScheme.text)       : [31, 41, 55];
  const muted: [number, number, number] = colorScheme?.secondary  ? hexToRgb(colorScheme.secondary)  : [100, 116, 139];
  const boxBg: [number, number, number] = colorScheme?.background ? hexToRgb(colorScheme.background) : [248, 250, 252];
  const danger: [number, number, number] = [185, 28, 28];

  const companyName = template?.templateCompanyName || rawCompanyName || 'Your Company';

  // ── HEADER: logo + company name (left), title + meta (right) ──────────────
  const logoSize = 24;
  const logoX = margin;
  const logoY = 10;
  let textX = margin;

  const logoSource = template?.templateLogo;
  const logoBase64 = template?.logoBase64;
  let hasLogo = false;

  if (logoBase64) {
    try { doc.addImage(logoBase64, 'PNG', logoX, logoY, logoSize, logoSize); hasLogo = true; } catch { /* skip */ }
  } else if (logoSource) {
    try {
      const url = logoSource.startsWith('http')
        ? logoSource
        : `${window.location.origin.replace(':3000', ':5002')}/${logoSource}`;
      doc.addImage(url, 'PNG', logoX, logoY, logoSize, logoSize);
      hasLogo = true;
    } catch { /* skip */ }
  }
  if (hasLogo) textX = logoX + logoSize + 6;

  const titleReserve = 85;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...navy);
  const nameLines = doc.splitTextToSize(companyName.toUpperCase(), pageWidth - textX - titleReserve);
  let nameY = logoY + 8;
  nameLines.forEach((line: string) => {
    doc.text(line, textX, nameY);
    nameY += 6;
  });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(...navy);
  doc.text('GOODS RECEIPT NOTE', pageWidth - margin, 20, { align: 'right' });

  const metaRows = [
    { label: 'GRN #:',   value: grn.receiptNumber },
    { label: 'Received:', value: formatDate(grn.receivedDate) },
    { label: 'Status:',  value: grn.status },
  ];
  let metaY = 28;
  doc.setFontSize(9);
  metaRows.forEach((row) => {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...muted);
    doc.text(row.label, pageWidth - margin - 55, metaY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...text);
    doc.text(row.value, pageWidth - margin, metaY, { align: 'right' });
    metaY += 6;
  });

  let yPos = Math.max(nameY, metaY, logoY + logoSize) + 6;
  doc.setDrawColor(...navy);
  doc.setLineWidth(0.6);
  doc.line(margin, yPos, pageWidth - margin, yPos);

  // ── INFO PANELS: Receipt Details (left) + Vendor (right) ───────────────────
  yPos += 8;
  const barW = 3;
  const boxW = pageWidth - margin * 2;
  const gap = 6;
  const panelW = (boxW - gap) / 2;

  const receiptDetailLines: string[] = [];
  receiptDetailLines.push(`Received By: ${grn.receivedBy}`);
  if (grn.warehouse) {
    receiptDetailLines.push(`Warehouse: ${grn.warehouse.code ? `${grn.warehouse.code} - ${grn.warehouse.name}` : grn.warehouse.name}`);
  }
  if (grn.purchaseOrder) {
    receiptDetailLines.push(`PO Number: ${grn.purchaseOrder.poNumber}`);
  }

  const vendor = grn.purchaseOrder?.vendor;
  const vendorLines: string[] = [];
  if (vendor) {
    vendorLines.push(vendor.name || 'N/A');
    const vendorContact = [vendor.email, vendor.phone].filter(Boolean).join(' | ');
    if (vendorContact) vendorLines.push(vendorContact);
  }

  const panelH = 8 + Math.max(receiptDetailLines.length, vendorLines.length || 1) * 5 + 5;

  const drawPanel = (x: number, heading: string, lines: string[], emphasizeFirst: boolean) => {
    doc.setFillColor(...boxBg);
    doc.rect(x, yPos, panelW, panelH, 'F');
    doc.setFillColor(...navy);
    doc.rect(x, yPos, barW, panelH, 'F');

    const cx = x + barW + 6;
    let cy = yPos + 7;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...muted);
    doc.text(heading, cx, cy);
    cy += 6;

    lines.forEach((line, i) => {
      doc.setFont('helvetica', i === 0 && emphasizeFirst ? 'bold' : 'normal');
      doc.setFontSize(i === 0 && emphasizeFirst ? 10.5 : 9);
      doc.setTextColor(...(i === 0 && emphasizeFirst ? text : muted));
      const wrapped = doc.splitTextToSize(line, panelW - barW - 10);
      doc.text(wrapped, cx, cy);
      cy += wrapped.length * 4.5;
    });
  };

  drawPanel(margin, 'RECEIPT DETAILS', receiptDetailLines, false);
  drawPanel(margin + panelW + gap, 'VENDOR', vendorLines.length ? vendorLines : ['N/A'], true);

  yPos += panelH + 8;

  // ── ITEMS TABLE ──────────────────────────────────────────────────────────
  const tableLeft  = margin;
  const tableRight = pageWidth - margin;
  const tableW     = tableRight - tableLeft;
  const headerH    = 10;
  const colW = { product: 32, ordered: 16, prev: 16, received: 18, rejected: 16, serials: 42, notes: 40 };

  const drawTableHeader = (y: number) => {
    doc.setFillColor(...navy);
    doc.rect(tableLeft, y, tableW, headerH, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(255, 255, 255);
    let hx = tableLeft;
    doc.text('PRODUCT', hx + 3, y + 6.5);
    hx += colW.product;
    doc.text('ORDERED', hx + 3, y + 6.5);
    hx += colW.ordered;
    doc.text('PREV.', hx + 3, y + 6.5);
    hx += colW.prev;
    doc.text('RECEIVED', hx + 3, y + 6.5);
    hx += colW.received;
    doc.text('REJECTED', hx + 3, y + 6.5);
    hx += colW.rejected;
    doc.text('SERIAL NUMBERS', hx + 3, y + 6.5);
    hx += colW.serials;
    doc.text('NOTES', hx + 3, y + 6.5);
  };

  drawTableHeader(yPos);
  yPos += headerH;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  (grn.items || []).forEach((item) => {
    const poItem = item.poItem;
    const product = poItem?.product;
    const orderedQty = poItem?.quantity || 0;
    const previouslyReceived = poItem?.receivedQuantity || 0;

    const productLines = doc.splitTextToSize(product?.name || 'N/A', colW.product - 5);
    const serialLines = doc.splitTextToSize(item.serialNumbers || '-', colW.serials - 5);
    const notesLines = doc.splitTextToSize(item.notes || '-', colW.notes - 5);
    const lineCount = Math.max(productLines.length, serialLines.length, notesLines.length);
    const rowH = Math.max(11, lineCount * 4 + 6);

    if (yPos + rowH > pageHeight - 65) {
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(tableLeft, yPos, tableRight, yPos);

      doc.addPage();
      yPos = 20;
      drawTableHeader(yPos);
      yPos += headerH;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
    }

    const rowStartY = yPos + 5;
    doc.setTextColor(...text);
    doc.text(productLines, tableLeft + 3, rowStartY);

    let cx = tableLeft + colW.product;
    doc.text(orderedQty.toString(), cx + 3, rowStartY);
    cx += colW.ordered;
    doc.text(previouslyReceived.toString(), cx + 3, rowStartY);
    cx += colW.prev;
    doc.setTextColor(...navy);
    doc.text(item.quantityReceived.toString(), cx + 3, rowStartY);
    doc.setTextColor(...text);
    cx += colW.received;
    if (item.quantityRejected > 0) {
      doc.setTextColor(...danger);
      doc.text(item.quantityRejected.toString(), cx + 3, rowStartY);
      doc.setTextColor(...text);
    } else {
      doc.text('0', cx + 3, rowStartY);
    }
    cx += colW.rejected;
    doc.setTextColor(...muted);
    doc.text(serialLines, cx + 3, rowStartY);
    cx += colW.serials;
    doc.text(notesLines, cx + 3, rowStartY);

    yPos += rowH;

    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(tableLeft, yPos, tableRight, yPos);
  });

  // ── SUMMARY + RECEIVED bar ──────────────────────────────────────────────
  yPos += 8;
  const totalOrdered = grn.items?.reduce((sum, item) => sum + (item.poItem?.quantity || 0), 0) || 0;
  const totalReceived = grn.items?.reduce((sum, item) => sum + item.quantityReceived, 0) || 0;
  const totalRejected = grn.items?.reduce((sum, item) => sum + (item.quantityRejected || 0), 0) || 0;

  const summaryLeft = pageWidth - margin - 95;
  const summaryRight = pageWidth - margin;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...muted);
  doc.text('Total Ordered', summaryLeft, yPos);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...text);
  doc.text(totalOrdered.toString(), summaryRight, yPos, { align: 'right' });
  yPos += 6.5;

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...muted);
  doc.text('Total Rejected', summaryLeft, yPos);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...danger);
  doc.text(totalRejected.toString(), summaryRight, yPos, { align: 'right' });
  yPos += 6.5;

  yPos += 1.5;
  const barH = 11;
  doc.setFillColor(...navy);
  doc.rect(summaryLeft, yPos, summaryRight - summaryLeft, barH, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(255, 255, 255);
  doc.text('Total Received', summaryLeft + 4, yPos + barH / 2 + 3.5);
  doc.text(totalReceived.toString(), summaryRight - 4, yPos + barH / 2 + 3.5, { align: 'right' });

  // ── NOTES ────────────────────────────────────────────────────────────────
  yPos += barH + 10;

  if (grn.notes) {
    if (yPos > pageHeight - 60) {
      doc.addPage();
      yPos = 20;
    }
    const boxPad = 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const lines = doc.splitTextToSize(grn.notes, boxW - boxPad * 2);
    const h = 10 + lines.length * 4.5 + 6;

    doc.setFillColor(...boxBg);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.rect(margin, yPos, boxW, h, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...text);
    doc.text('NOTES', margin + boxPad, yPos + 8);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...muted);
    doc.text(lines, margin + boxPad, yPos + 14.5);

    yPos += h + 10;
  }

  // ── SIGNATURE LINES ──────────────────────────────────────────────────────
  if (yPos > pageHeight - 45) {
    doc.addPage();
    yPos = 20;
  }
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, margin + 70, yPos);
  doc.line(pageWidth - margin - 70, yPos, pageWidth - margin, yPos);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...muted);
  doc.text('Received By', margin + 20, yPos + 5);
  doc.text('Authorized By', pageWidth - margin - 45, yPos + 5);

  // ── FOOTER ───────────────────────────────────────────────────────────────
  const footerContactLine = companyAddress;
  const footerReachLine = [companyPhone, companyEmail].filter(Boolean).join('  |  ');

  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 22, pageWidth - margin, pageHeight - 22);

    let footY = pageHeight - 17;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...text);
    doc.text(companyName, pageWidth / 2, footY, { align: 'center' });
    footY += 4.5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...muted);
    if (footerContactLine) {
      doc.text(footerContactLine, pageWidth / 2, footY, { align: 'center' });
      footY += 4;
    }
    if (footerReachLine) {
      doc.text(footerReachLine, pageWidth / 2, footY, { align: 'center' });
    }
  }

  doc.save(`GRN_${grn.receiptNumber}.pdf`);
};
